import { ExportAssetType } from '../../../asset/util/useAddPermissions';
import React, { Fragment, ReactNode, useState } from 'react';
import { StyledCompactTable } from '../../../configuration/util/utils';
import { Select } from 'antd';
import Button from '../../Button';
import { exportAssetTypeSubTypeKey, fullAssetName } from './util';
import { Check, X } from 'react-feather';
import { NewAsset, useAssetTranslation } from '@mss/mss-component-library';

type CorrectAssetSubTypeProps<AssetType> = {
  assets: AssetType[];
  type: ExportAssetType;
  deleteAsset: (tempId: number) => void;
  updateAssetType: (tempId: number, type: string) => void;
};

function CorrectAssetSubType<AssetType extends NewAsset>({
  assets,
  type,
  deleteAsset,
  updateAssetType,
}: CorrectAssetSubTypeProps<AssetType>) {
  const { t } = useAssetTranslation();
  const assetSubType = exportAssetTypeSubTypeKey<AssetType>(type);
  const [correctedIds, setCorrectedIds] = useState<number[]>([]);
  return (
    <>
      <h4>{t('Unknown asset type')}</h4>
      <p>
        {t(
          'The following assets can not be imported as is due to a missing or incorrect asset type. The options for ((assetTypeKey)) are',
          { assetTypeKey: assetSubType.key }
        )}
        :{' '}
        {assetSubType.options.reduce((acc, option, i) => {
          const isLast = i === assetSubType.options.length - 1;
          const isFirst = i === 0;
          acc.push(
            <Fragment key={option}>
              {isLast ? ' & ' : isFirst ? '' : ', '}
              <span className="font-bold">{option}</span> (
              {t(assetSubType.labels[option] ?? option)})
            </Fragment>
          );
          return acc;
        }, [] as ReactNode[])}
        .
      </p>

      <StyledCompactTable
        dataSource={assets}
        rowClassName="cursor-default"
        pagination={assets.length > 10}
        rowKey="tempId"
        columns={[
          {
            key: 'corrected',
            width: 32,
            dataIndex: 'tempId',
            render: (tempId: number) =>
              correctedIds.includes(tempId) ? (
                <Check size={16} className="text-success" />
              ) : (
                <X size={16} className="text-danger" />
              ),
          },
          {
            dataIndex: 'name',
            key: 'name',
            title: t('Name'),
            width: '40%',
            render: (value: string) => value || '-',
          },
          {
            dataIndex: assetSubType.key,
            key: assetSubType.key,
            title: `${t(fullAssetName(type))} ${t('type')}`,
            render: (_: string, row: AssetType & { tempId: number }) => (
              <Select
                placeholder={t('Please select the type')}
                className="w-full"
                onChange={value => {
                  updateAssetType(row.tempId, value);
                  setCorrectedIds(ids =>
                    Array.from(new Set([...ids, row.tempId]))
                  );
                }}
                options={assetSubType.options.map(option => ({
                  label: t(assetSubType.labels[option] ?? option),
                  value: option,
                }))}
              />
            ),
          },
          {
            dataIndex: 'tempId',
            render: (tempId: number) => (
              <Button
                onClick={() => deleteAsset(tempId)}
                className="border-0 w-full bg-transparent hover:bg-danger"
              >
                {t('Delete')}
              </Button>
            ),
            key: 'tempId',
            title: t('Delete from import'),
            width: 150,
          },
        ]}
      />
    </>
  );
}

export default CorrectAssetSubType;
